import React from 'react';
import { Box } from '@mui/material';
import { ingestorCoverage } from './ingestorCoverage';
import { ingest, tabular } from './ingestionTokens';

/**
 * What this connector is known to scrape, and what it never supplies.
 * Sits on the connector overview. Copy comes from `ingestorCoverage`.
 */
export const IngestorCoveragePanel: React.FC<{ brandId: string }> = ({ brandId }) => {
  const coverage = ingestorCoverage(brandId);

  return (
    <Box sx={{ padding: '0 24px 18px' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <Box sx={{ fontFamily: ingest.font.sans, fontSize: '13px', fontWeight: 600, color: ingest.color.ink }}>
          What this connector scrapes
        </Box>
        <Box sx={{ fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint, ...tabular }}>
          {coverage.source}
        </Box>
      </Box>

      {coverage.unrecorded ? (
        <Box
          sx={{
            border: `1px solid ${ingest.color.borderInner}`,
            borderRadius: '9px',
            padding: '14px',
            fontFamily: ingest.font.sans,
            fontSize: '12.5px',
            color: ingest.color.faintAlt,
          }}
        >
          What this connector can and cannot scrape has not been recorded.
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: '14px',
          }}
        >
          <CoverageColumn title="Can scrape" items={coverage.publishes} tone="can" />
          <CoverageColumn title="Not from this source" items={coverage.unavailable} tone="cannot" />
        </Box>
      )}
    </Box>
  );
};

const CoverageColumn: React.FC<{
  title: string;
  items: string[];
  tone: 'can' | 'cannot';
}> = ({ title, items, tone }) => {
  const dot = tone === 'can' ? ingest.color.success : ingest.color.warning;
  return (
    <Box sx={{ border: `1px solid ${ingest.color.borderInner}`, borderRadius: '9px', padding: '13px 15px' }}>
      <Box
        component="h3"
        sx={{
          margin: 0,
          fontFamily: ingest.font.sans,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          lineHeight: 1.2,
          color: ingest.color.faintAlt,
        }}
      >
        {title}
      </Box>
      <Box component="ul" aria-label={title} sx={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
        {items.map((item) => (
          <Box
            component="li"
            key={item}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              fontFamily: ingest.font.sans,
              fontSize: '12.5px',
              lineHeight: 1.45,
              color: ingest.color.body,
              '& + &': { marginTop: '7px' },
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: '6px',
                height: '6px',
                borderRadius: '4px',
                background: dot,
                flexShrink: 0,
                marginTop: '6px',
              }}
            />
            <span>{item}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
